import { LOAI_DINH_MUC } from "@/core/constants/const";
import { swalNotification } from "@/core/helpers/utils";
import {
    IVatTuResponseBase,
    IVatTuXuatKhoResponseBase,
} from "@/core/interfaces/ApiResponses";
import { IVatTuSelectedInKho } from "@/core/interfaces/Models";
import PhieuXuatKhoService from "@/core/services/Receipt.service";
import type {
    BbgtKhoItem,
    BbgtVatTuSelectionRow,
} from "@/types/phieu-xuat-kho";
import { computed, ref, watch, type Ref } from "vue";
import { loaiDinhMucToApiParam } from "../constants";
import type {
    IPhieuXuatKhoSuppliesQuantityRow,
    PhieuXuatKhoLoaiPhieu,
} from "../types";
import { isThuHoiVatTu } from "./normalizeTableRow";

function resolveManualSoLuongKho(
  item: IVatTuXuatKhoResponseBase,
  idKho?: number
): number | undefined {
  if (item.so_luong_kho != null && item.so_luong_kho !== "") {
    return +item.so_luong_kho;
  }

  if (idKho != null) {
    return resolveSavedSoLuongKho(item, idKho);
  }

  return undefined;
}

function getManualTonKho(
  item: IVatTuXuatKhoResponseBase,
  idKho?: number
): number {
  const fromSoLuongKho = resolveManualSoLuongKho(item, idKho);

  if (fromSoLuongKho != null) {
    return fromSoLuongKho;
  }

  const extended = item as IVatTuXuatKhoResponseBase & {
    so_luong_ton?: number;
  };
  const pivotTon = (item.pivot as { so_luong_ton?: number })?.so_luong_ton;

  if (extended.so_luong_ton != null) {
    return +extended.so_luong_ton;
  }

  if (pivotTon != null) {
    return +pivotTon;
  }

  return +item.pivot.so_luong;
}

async function fetchTonKhoForItem(idVatTu: number, idKho: number) {
  const {
    data: { data },
  } = await PhieuXuatKhoService.getTonKho(idVatTu, idKho);

  return {
    don_gia: +data.don_gia,
    so_luong_ton: +data.so_luong,
  };
}

function mapManualSelectingItem(
  item: IVatTuResponseBase,
  idKho: number
): IVatTuXuatKhoResponseBase {
  const withTon = item as IVatTuResponseBase & {
    so_luong_ton?: number;
    pivot?: { don_gia?: number };
  };

  return {
    ...item,
    so_luong_ton: withTon.so_luong_ton,
    pivot: {
      don_gia: withTon.pivot?.don_gia ?? item.don_gia,
      id_kho: idKho,
      id_phieu_xuat_kho: 0,
      id_vat_tu: item.id,
      so_luong: item.so_luong,
      so_luong_ton: withTon.so_luong_ton,
    } as IVatTuXuatKhoResponseBase["pivot"] & { so_luong_ton?: number },
  } as IVatTuXuatKhoResponseBase;
}

function resolveBbgtDonViTinh(row: BbgtVatTuSelectionRow): string {
  return row.don_vi_tinh ?? row.ĐVT ?? "";
}

function resolveSavedSoLuongKho(
  saved: IVatTuXuatKhoResponseBase,
  idKho: number
): number | undefined {
  const khoMatch = saved.kho?.find((k) => +k.id_kho === idKho);

  if (khoMatch?.so_luong_kho != null) {
    return +khoMatch.so_luong_kho;
  }

  return undefined;
}

function resolveSoLuongKho(
  listRow: BbgtVatTuSelectionRow | undefined,
  saved: IVatTuXuatKhoResponseBase | undefined,
  idKho: number
): number {
  if (listRow != null) {
    return listRow.so_luong_kho;
  }

  if (saved) {
    const fromSaved = resolveSavedSoLuongKho(saved, idKho);

    if (fromSaved != null) {
      return fromSaved;
    }
  }

  return 0;
}

function mapSavedBbgtSupplyItem(
  saved: IVatTuXuatKhoResponseBase,
  idKho: number,
  listRow?: BbgtVatTuSelectionRow
): IVatTuXuatKhoResponseBase {
  return {
    ...saved,
    id: saved.id,
    ten_vat_tu: saved.ten_vat_tu,
    ma_vat_tu: saved.ma_vat_tu ?? listRow?.ma_vat_tu ?? "",
    don_vi_tinh:
      saved.don_vi_tinh ??
      (listRow ? resolveBbgtDonViTinh(listRow) : ""),
    don_gia: saved.pivot.don_gia ?? listRow?.don_gia_kho ?? saved.don_gia,
    thu_hoi:
      saved.thu_hoi ??
      (listRow ? isThuHoiVatTu(listRow.thu_hoi) : false),
    vat_tu_thu_hoi:
      saved.vat_tu_thu_hoi ?? listRow?.vat_tu_thu_hoi ?? undefined,
    pivot: {
      ...saved.pivot,
      id_kho: idKho,
      id_vat_tu: saved.id,
      so_luong: +saved.pivot.so_luong,
      don_gia: saved.pivot.don_gia ?? listRow?.don_gia_kho,
      sl_tai_sd: saved.pivot.sl_tai_sd,
      sl_mat: saved.pivot.sl_mat,
      sl_phe_lieu: saved.pivot.sl_phe_lieu,
      kg_phe_lieu: saved.pivot.kg_phe_lieu,
      ghi_chu: saved.pivot.ghi_chu,
    },
  } as IVatTuXuatKhoResponseBase;
}

function buildQuantityRowFromSavedBbgtEdit(
  saved: IVatTuXuatKhoResponseBase,
  listRow: BbgtVatTuSelectionRow | undefined,
  idKho: number
): IPhieuXuatKhoSuppliesQuantityRow {
  const soLuongKho = resolveSoLuongKho(listRow, saved, idKho);
  const soLuong = +saved.pivot.so_luong;
  const listBase = listRow
    ? buildQuantityRowFromBbgt(listRow, listRow.chenh_lech)
    : null;

  return {
    id_vat_tu: saved.id,
    so_luong_trong_kho_ban_dau: soLuongKho,
    so_luong_se_lay: soLuong,
    so_luong_can: listBase?.so_luong_can ?? soLuong,
    don_gia_trong_kho:
      saved.pivot.don_gia ?? listBase?.don_gia_trong_kho ?? 0,
    chenh_lech: listBase?.chenh_lech,
    so_luong_goi_y: listBase?.so_luong_goi_y,
    so_luong_toi_thieu: soLuong,
    dinh_muc: listBase?.dinh_muc,
    da_dung: listBase?.da_dung,
    tai_sd: saved.pivot.sl_tai_sd ?? soLuong,
    mat: saved.pivot.sl_mat ?? 0,
    so_luong_phe_lieu: saved.pivot.sl_phe_lieu ?? 0,
    quy_doi_kg: saved.pivot.kg_phe_lieu ?? 0,
    ma_vt_thu_hoi:
      saved.vat_tu_thu_hoi?.ma_vat_tu ??
      listBase?.ma_vt_thu_hoi ??
      "",
    ghi_chu: saved.pivot.ghi_chu ?? "",
  };
}

function mapBbgtRowToSupplyItem(
  row: BbgtVatTuSelectionRow,
  idKho: number
): IVatTuXuatKhoResponseBase {
  return {
    id: row.id_vat_tu,
    ten_vat_tu: row.ten_vat_tu,
    ma_vat_tu: row.ma_vat_tu ?? "",
    don_vi_tinh: resolveBbgtDonViTinh(row),
    don_gia: row.don_gia_kho,
    thu_hoi: isThuHoiVatTu(row.thu_hoi),
    vat_tu_thu_hoi: row.vat_tu_thu_hoi ?? undefined,
    pivot: {
      id_kho: idKho,
      id_phieu_xuat_kho: 0,
      id_vat_tu: row.id_vat_tu,
      so_luong: row.so_luong_kho,
      don_gia: row.don_gia_kho,
    },
  } as IVatTuXuatKhoResponseBase;
}

function resolveBbgtKhoItem(
  khoOptions: BbgtKhoItem[],
  idKho: number
): BbgtKhoItem | null {
  return khoOptions.find(({ id }) => Number(id) === Number(idKho)) ?? null;
}

function buildQuantityRowFromBbgt(
  row: BbgtVatTuSelectionRow,
  conThieu: number
): IPhieuXuatKhoSuppliesQuantityRow {
  const cappedConThieu = Math.max(0, conThieu);
  const suggestedQty =
    row.so_luong_goi_y != null && !Number.isNaN(Number(row.so_luong_goi_y))
      ? Number(row.so_luong_goi_y)
      : cappedConThieu;
  const soLuongSeLay = Math.min(
    suggestedQty,
    cappedConThieu,
    Number(row.so_luong_kho) || 0
  );

  return {
    id_vat_tu: row.id_vat_tu,
    so_luong_trong_kho_ban_dau: row.so_luong_kho,
    so_luong_se_lay: soLuongSeLay,
    so_luong_can: row.chenh_lech,
    don_gia_trong_kho: row.don_gia_kho,
    chenh_lech: row.chenh_lech,
    so_luong_goi_y: row.so_luong_goi_y,
    so_luong_toi_thieu: row.so_luong_toi_thieu,
    dinh_muc: row.dinh_muc,
    da_dung: row.da_dung,
    tai_sd: soLuongSeLay,
    mat: 0,
    so_luong_phe_lieu: 0,
    quy_doi_kg: 0,
    ma_vt_thu_hoi: row.vat_tu_thu_hoi?.ma_vat_tu ?? "",
  };
}

export function usePhieuXuatKhoSupplies(options: {
  loaiPhieu: Ref<PhieuXuatKhoLoaiPhieu>;
  idBienBan?: Ref<number | undefined>;
  loaiDinhMuc?: Ref<LOAI_DINH_MUC | undefined>;
  idKho?: Ref<number | undefined>;
  excludeIdPhieuXuat?: Ref<number | undefined>;
  initialVatTuItems?: Ref<IVatTuXuatKhoResponseBase[] | undefined>;
  isEditMode?: Ref<boolean>;
}) {
  const khoOptions = ref<BbgtKhoItem[]>([]);
  const selectingKhoId = ref<number>(-1);
  const selectingKhoItem = ref<BbgtKhoItem | null>(null);
  const bbgtSelectionRows = ref<BbgtVatTuSelectionRow[]>([]);
  const vatTuSelectedInKho = ref<IVatTuSelectedInKho[]>([]);
  const vatTuItems = ref<IVatTuXuatKhoResponseBase[]>([]);
  const quantityRows = ref<IPhieuXuatKhoSuppliesQuantityRow[]>([]);
  const isLoadingKho = ref(false);
  const isLoadingVatTu = ref(false);

  const isBbgtMode = computed(() => options.loaiPhieu.value === 0);
  const isManualMode = computed(() => options.loaiPhieu.value !== 0);

  const getConThieu = (idVatTu: number) =>
    vatTuSelectedInKho.value.find(({ id_vat_tu }) => id_vat_tu === idVatTu)
      ?.con_thieu ?? 0;

  const initVatTuSelectedInKho = (items: BbgtVatTuSelectionRow[]) => {
    vatTuSelectedInKho.value = items.map((row) => ({
      id_vat_tu: row.id_vat_tu,
      so_luong_can: row.chenh_lech,
      con_thieu: row.chenh_lech,
      kho_arr: [],
    }));
  };

  const initVatTuSelectedInKhoFromMerge = (
    savedItems: IVatTuXuatKhoResponseBase[],
    listItems: BbgtVatTuSelectionRow[]
  ) => {
    const listById = new Map(listItems.map((row) => [row.id_vat_tu, row]));
    const savedIds = new Set(savedItems.map(({ id }) => id));

    vatTuSelectedInKho.value = [
      ...savedItems.map((saved) => {
        const list = listById.get(saved.id);

        return {
          id_vat_tu: saved.id,
          so_luong_can: list?.chenh_lech ?? +saved.pivot.so_luong,
          con_thieu: list?.chenh_lech ?? +saved.pivot.so_luong,
          kho_arr: [],
        };
      }),
      ...listItems
        .filter((row) => !savedIds.has(row.id_vat_tu))
        .map((row) => ({
          id_vat_tu: row.id_vat_tu,
          so_luong_can: row.chenh_lech,
          con_thieu: row.chenh_lech,
          kho_arr: [],
        })),
    ];
  };

  const buildQuantityRowsFromBbgt = (
    items: BbgtVatTuSelectionRow[],
    idKho: number
  ) => {
    const savedItems =
      options.isEditMode?.value && options.initialVatTuItems?.value?.length
        ? options.initialVatTuItems.value
        : [];

    if (savedItems.length) {
      const listById = new Map(items.map((row) => [row.id_vat_tu, row]));
      const savedIds = new Set(savedItems.map(({ id }) => id));

      const savedEntries = savedItems.map((saved) => ({
        saved,
        list: listById.get(saved.id),
      }));

      const listOnlyEntries = items
        .filter((row) => !savedIds.has(row.id_vat_tu) && row.so_luong_kho > 0)
        .map((list) => list);

      quantityRows.value = [
        ...savedEntries.map(({ saved, list }) =>
          buildQuantityRowFromSavedBbgtEdit(saved, list, idKho)
        ),
        ...listOnlyEntries.map((list) =>
          buildQuantityRowFromBbgt(list, list.chenh_lech)
        ),
      ];

      vatTuItems.value = [
        ...savedEntries.map(({ saved, list }) =>
          mapSavedBbgtSupplyItem(saved, idKho, list)
        ),
        ...listOnlyEntries.map((list) => mapBbgtRowToSupplyItem(list, idKho)),
      ];

      initVatTuSelectedInKhoFromMerge(savedItems, items);

      return;
    }

    const tableRows = items.filter((row) => row.so_luong_kho > 0);

    quantityRows.value = tableRows.map((row) =>
      buildQuantityRowFromBbgt(row, row.chenh_lech)
    );

    vatTuItems.value = tableRows.map((row) =>
      mapBbgtRowToSupplyItem(row, idKho)
    );

    initVatTuSelectedInKho(items);
  };

  const resetBbgtVatTuState = () => {
    bbgtSelectionRows.value = [];
    quantityRows.value = [];
    vatTuItems.value = [];
    vatTuSelectedInKho.value = [];
    selectingKhoItem.value = null;
  };

  const fetchBbgtKhoList = async () => {
    if (!options.idBienBan?.value || !options.loaiDinhMuc?.value) {
      return;
    }

    isLoadingKho.value = true;
    khoOptions.value = [];
    selectingKhoId.value = -1;
    resetBbgtVatTuState();

    try {
      const {
        data: { data },
      } = await PhieuXuatKhoService.getDanhSachKhoFromBBGT({
        id_bien_ban: options.idBienBan.value,
        loai_dinh_muc: loaiDinhMucToApiParam(options.loaiDinhMuc.value),
      });

      khoOptions.value = Array.isArray(data) ? data : [];
    } finally {
      isLoadingKho.value = false;
    }
  };

  const fetchBbgtVatTuByKho = async (idKho: number) => {
    if (!options.idBienBan?.value || !options.loaiDinhMuc?.value) {
      return;
    }

    isLoadingVatTu.value = true;

    try {
      const excludeIds = options.excludeIdPhieuXuat?.value
        ? [options.excludeIdPhieuXuat.value]
        : undefined;

      const { data: response } =
        await PhieuXuatKhoService.getDanhSachVatTuFromBBGT({
          id_bien_ban: options.idBienBan.value,
          id_kho: idKho,
          loai_dinh_muc: loaiDinhMucToApiParam(options.loaiDinhMuc.value),
          exclude_id_phieu_xuat: excludeIds,
        });

      const items = Array.isArray(response.data) ? response.data : [];

      bbgtSelectionRows.value = items;
      buildQuantityRowsFromBbgt(items, idKho);

      selectingKhoItem.value = resolveBbgtKhoItem(khoOptions.value, idKho);

      const extraMessage = response.extra?.message?.trim();

      if (extraMessage) {
        await swalNotification(extraMessage, "warning");
      }
    } finally {
      isLoadingVatTu.value = false;
    }
  };

  const onBbgtKhoChange = async (idKho: number) => {
    const normalizedIdKho = Number(idKho) || 0;
    selectingKhoId.value = normalizedIdKho;

    if (normalizedIdKho <= 0) {
      resetBbgtVatTuState();

      return;
    }

    selectingKhoItem.value = resolveBbgtKhoItem(
      khoOptions.value,
      normalizedIdKho
    );
    bbgtSelectionRows.value = [];
    quantityRows.value = [];
    vatTuItems.value = [];
    vatTuSelectedInKho.value = [];

    await fetchBbgtVatTuByKho(normalizedIdKho);
  };

  const buildVatTuSelectedInKhoPayload = (): IVatTuSelectedInKho[] => {
    if (!selectingKhoItem.value) {
      return [];
    }

    const result: IVatTuSelectedInKho[] = [];

    vatTuSelectedInKho.value.forEach((vatTuCan) => {
      const row = quantityRows.value.find(
        ({ id_vat_tu }) => id_vat_tu === vatTuCan.id_vat_tu
      );

      if (!row || row.so_luong_se_lay <= 0) {
        return;
      }

      result.push({
        ...vatTuCan,
        kho_arr: [
          {
            kho_id: selectingKhoItem.value?.id ?? 0,
            ten_kho: selectingKhoItem.value?.ten_kho ?? "",
            tong_so_trong_kho: row.so_luong_trong_kho_ban_dau,
            da_lay: +row.so_luong_se_lay,
            con_lai: row.so_luong_trong_kho_ban_dau - row.so_luong_se_lay,
            sl_tai_sd: row.tai_sd,
            sl_mat: row.mat,
            sl_phe_lieu: row.so_luong_phe_lieu,
            kg_phe_lieu: row.quy_doi_kg,
          },
        ],
      });
    });

    return result;
  };

  const syncBbgtItemsFromQuantityRows = () => {
    vatTuItems.value = vatTuItems.value.map((item) => {
      const row = quantityRows.value.find(
        ({ id_vat_tu }) => id_vat_tu === item.id
      );

      if (!row) {
        return item;
      }

      return {
        ...item,
        pivot: {
          ...item.pivot,
          so_luong: row.so_luong_se_lay,
          don_gia: row.don_gia_trong_kho,
          sl_tai_sd: row.tai_sd,
          sl_mat: row.mat,
          sl_phe_lieu: row.so_luong_phe_lieu,
          kg_phe_lieu: row.quy_doi_kg,
          ghi_chu: row.ghi_chu,
        },
      };
    });
  };

  const addManualVatTuItems = (selectingVatTuItems: IVatTuResponseBase[]) => {
    const idKho = options.idKho?.value ?? 0;
    const newItems = selectingVatTuItems.map((item) =>
      mapManualSelectingItem(item, idKho)
    );

    vatTuItems.value = [...vatTuItems.value, ...newItems];
    rebuildManualQuantityRows();
  };

  const removeManualVatTuItem = (idVatTu: number) => {
    vatTuItems.value = vatTuItems.value.filter(({ id }) => id !== idVatTu);
    rebuildManualQuantityRows();
  };

  const rebuildManualQuantityRows = () => {
    const zeroThuHoiFields = options.loaiPhieu.value === 2;
    const idKho = options.idKho?.value;

    quantityRows.value = vatTuItems.value.map((item) => {
      const soLuongTon = getManualTonKho(item, idKho);

      return {
        id_vat_tu: item.id,
        so_luong_trong_kho_ban_dau: soLuongTon,
        so_luong_se_lay: item.pivot.so_luong,
        so_luong_can: item.pivot.so_luong,
        don_gia_trong_kho: item.pivot.don_gia ?? item.don_gia,
        tai_sd: zeroThuHoiFields
          ? 0
          : item.pivot.sl_tai_sd ?? item.pivot.so_luong,
        mat: zeroThuHoiFields ? 0 : item.pivot.sl_mat ?? 0,
        so_luong_phe_lieu: zeroThuHoiFields ? 0 : item.pivot.sl_phe_lieu ?? 0,
        quy_doi_kg: zeroThuHoiFields ? 0 : item.pivot.kg_phe_lieu ?? 0,
        ma_vt_thu_hoi: zeroThuHoiFields
          ? ""
          : item.vat_tu_thu_hoi?.ma_vat_tu ?? "",
        ghi_chu: item.pivot.ghi_chu ?? "",
      };
    });
  };

  const refreshManualItemsTonKho = async (idKho: number) => {
    await Promise.all(
      vatTuItems.value.map(async (item) => {
        try {
          const tonKho = await fetchTonKhoForItem(item.id, idKho);

          (
            item as IVatTuXuatKhoResponseBase & { so_luong_ton?: number }
          ).so_luong_ton = tonKho.so_luong_ton;

          item.pivot = {
            ...item.pivot,
            don_gia: tonKho.don_gia,
            so_luong_ton: tonKho.so_luong_ton,
          } as IVatTuXuatKhoResponseBase["pivot"] & { so_luong_ton?: number };
        } catch {
          // Keep existing values when ton-kho lookup fails.
        }
      })
    );
  };

  const syncManualItemsFromQuantityRows = () => {
    const zeroThuHoiFields = options.loaiPhieu.value === 2;

    vatTuItems.value = vatTuItems.value.map((item) => {
      const row = quantityRows.value.find(
        ({ id_vat_tu }) => id_vat_tu === item.id
      );

      if (!row) {
        return item;
      }

      return {
        ...item,
        pivot: {
          ...item.pivot,
          so_luong: row.so_luong_se_lay,
          sl_tai_sd: zeroThuHoiFields ? 0 : row.tai_sd,
          sl_mat: zeroThuHoiFields ? 0 : row.mat,
          sl_phe_lieu: zeroThuHoiFields ? 0 : row.so_luong_phe_lieu,
          kg_phe_lieu: zeroThuHoiFields ? 0 : row.quy_doi_kg,
          ghi_chu: row.ghi_chu,
        },
      };
    });
  };

  const loadInitialVatTuItems = async (items: IVatTuXuatKhoResponseBase[]) => {
    vatTuItems.value = [...items];

    const idKho = options.idKho?.value;
    const needsTonKhoRefresh =
      idKho != null &&
      items.some((item) => resolveManualSoLuongKho(item, idKho) == null);

    if (needsTonKhoRefresh) {
      await refreshManualItemsTonKho(idKho);
    }

    rebuildManualQuantityRows();
  };

  watch(
    () => options.initialVatTuItems?.value,
    (items) => {
      if (items?.length && isManualMode.value) {
        void loadInitialVatTuItems(items);
      }
    },
    { immediate: true }
  );

  return {
    addManualVatTuItems,
    bbgtSelectionRows,
    buildVatTuSelectedInKhoPayload,
    fetchBbgtKhoList,
    getConThieu,
    isBbgtMode,
    isLoadingKho,
    isLoadingVatTu,
    isManualMode,
    khoOptions,
    loadInitialVatTuItems,
    onBbgtKhoChange,
    quantityRows,
    removeManualVatTuItem,
    selectingKhoId,
    selectingKhoItem,
    syncBbgtItemsFromQuantityRows,
    syncManualItemsFromQuantityRows,
    vatTuItems,
    vatTuSelectedInKho,
  };
}
